import { useEffect, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

function fmtISO(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtLabel(d) {
  if (!d) return "Pick a date";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export default function DatePicker({ value, onChange, testid, placeholder = "Pick a date", minDate }) {
  const [blockedSet, setBlockedSet] = useState(new Set());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .get("/availability")
      .then((r) => setBlockedSet(new Set((r.data || []).map((b) => b.date))))
      .catch(() => {});
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const floor = minDate || today;

  const selected = value ? new Date(value + "T00:00:00") : undefined;

  const isDisabled = (d) => {
    const dd = new Date(d);
    dd.setHours(0, 0, 0, 0);
    if (dd < floor) return true;
    return blockedSet.has(fmtISO(dd));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full rounded-2xl bg-[#FFFBF0] border-2 border-[#3F2A56]/20 px-4 py-3 text-left text-[#2A1C3A] focus:outline-none focus:ring-2 focus:ring-[#4A6741] focus:border-[#4A6741] transition-colors flex items-center justify-between gap-2"
        >
          <span className={value ? "" : "text-[#5C4A73]/60"}>
            {value ? fmtLabel(selected) : placeholder}
          </span>
          <CalendarIcon className="w-4 h-4 text-[#3F2A56]/60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#FFFBF0] border-2 border-[#3F2A56]/20 rounded-2xl"
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (!d) return;
            onChange(fmtISO(d));
            setOpen(false);
          }}
          disabled={isDisabled}
          initialFocus
        />
        {blockedSet.size > 0 && (
          <div className="px-4 pb-3 pt-1 text-xs text-[#5C4A73]">
            Grayed-out dates are unavailable.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
